import React, { useEffect, useRef, useState } from 'react';
import { useGameStore } from '../store/gameStore';
import clsx from 'clsx';
import { CheckCircle, XCircle, AlertTriangle, Zap, Info, Terminal, Radio, ChevronUp, ChevronDown, GripVertical, ShieldAlert, Activity } from 'lucide-react';
import { Tooltip } from './Tooltip';

export const EventLog: React.FC = () => {
  const events = useGameStore(state => state.events);
  const blockedAction = useGameStore(state => state.blockedAction);
  const [width, setWidth] = useState(320);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dragStartX = useRef(0);
  const startWidth = useRef(0);

  // Auto-scroll to top when new events arrive
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (scrollRef.current && !isCollapsed) {
      scrollRef.current.scrollTop = 0;
    }
  }, [events, isCollapsed]);

  // Resize Logic
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      const deltaX = dragStartX.current - e.clientX; // Dragging left increases width
      const newWidth = Math.min(Math.max(250, startWidth.current + deltaX), window.innerWidth - 40);
      setWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.body.style.cursor = 'default';
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ew-resize';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    dragStartX.current = e.clientX;
    startWidth.current = width;
    e.preventDefault();
  };

  return (
    <div 
      ref={containerRef}
      className={clsx(
        "fixed bottom-4 right-4 z-40 flex flex-col transition-all duration-300 ease-in-out rounded-xl overflow-hidden pointer-events-auto",
        isDragging ? "transition-none" : ""
      )}
      style={{ 
        width: `${width}px`,
        height: isCollapsed ? 'auto' : '320px',
        maxHeight: '80vh'
      }}
    >
      {/* Glassmorphism Container */}
      <div className="absolute inset-0 glass-panel pointer-events-none" />

      {/* Resize Handle (Left Border) */}
      <div 
        className="absolute left-0 top-0 bottom-0 w-1.5 cursor-ew-resize hover:bg-ops-blue/50 transition-colors z-50 flex items-center justify-center group"
        onMouseDown={handleMouseDown}
      >
        <div className="h-8 w-0.5 bg-slate-600 group-hover:bg-ops-blue rounded-full" />
      </div>

      {/* Header */}
      <div className="relative z-10 flex items-center justify-between p-3 border-b border-slate-700/50 bg-slate-900/50 backdrop-blur-md cursor-move" onMouseDown={handleMouseDown}>
        <div className="flex items-center gap-2">
          <Terminal className="w-4 h-4 text-ops-blue" />
          <span className="text-xs font-bold text-slate-300 uppercase tracking-widest font-mono">Ops Log</span>
          <div className="flex items-center gap-1 ml-2 px-1.5 py-0.5 rounded bg-ops-green/10 border border-ops-green/20">
            <div className="w-1.5 h-1.5 rounded-full bg-ops-green animate-pulse" />
            <span className="text-[8px] font-bold text-ops-green uppercase tracking-wider">Live</span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button 
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-1 hover:bg-slate-800 rounded text-slate-400 hover:text-white transition-colors"
          >
            {isCollapsed ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Content Area */}
      {!isCollapsed && (
        <div className="relative z-10 flex-1 overflow-y-auto p-3 space-y-2 custom-scrollbar font-mono" ref={scrollRef}>
          
          {/* Blocked Action Warning (Sticky) */}
          {blockedAction && (
            <div className="bg-ops-red/10 border border-ops-red/30 p-3 rounded mb-3 animate-pulse">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-ops-red shrink-0 mt-0.5" />
                <div>
                  <div className="text-xs font-bold text-ops-red uppercase tracking-wider mb-1">Action Blocked</div>
                  <div className="text-xs text-slate-300">{blockedAction.reason}</div>
                </div>
              </div>
            </div>
          )}

          {/* Event Stream */}
          {events.length === 0 ? (
            <div className="text-center py-8 text-slate-600 text-sm italic">
              // Awaiting operational data...
            </div>
          ) : (
            [...events].reverse().map((event) => (
              <div 
                key={event.id} 
                className={clsx(
                  "relative pl-3 py-3 border-l-4 text-sm transition-all duration-300 animate-slide-in group hover:bg-white/5 rounded-r mb-2",
                  event.type === 'success' ? "border-ops-green bg-ops-green/5" :
                  event.type === 'failure' ? "border-ops-red bg-ops-red/5" :
                  event.type === 'info' ? "border-ops-blue bg-ops-blue/5" :
                  "border-ops-amber bg-ops-amber/5"
                )}
              >
                <div className="flex justify-between items-start mb-1">
                  <span className={clsx(
                    "font-bold uppercase tracking-wider text-xs",
                    event.type === 'success' ? "text-ops-green" :
                    event.type === 'failure' ? "text-ops-red" :
                    event.type === 'info' ? "text-ops-blue" :
                    "text-ops-amber"
                  )}>
                    {event.type === 'success' ? 'Success' : 
                     event.type === 'failure' ? 'Failure' : 
                     event.type === 'info' ? 'Intel' : 'Alert'}
                  </span>
                  <span className="text-[10px] text-slate-500 font-mono">
                    T-{event.turn}
                  </span>
                </div>
                <p className="text-slate-200 leading-relaxed font-medium">
                  {event.message}
                </p>
                {/* Hover Timestamp */}
                <div className="absolute right-2 bottom-1 opacity-0 group-hover:opacity-100 transition-opacity text-[9px] text-slate-600">
                  {new Date(event.timestamp).toLocaleTimeString([], { hour12: false })}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};
